import React, { useState, useEffect } from 'react';
import axios from 'axios';

import {
	useLazyLoad,
	useSocketContext,
	LRS_OBJECT_TYPES,
	SCOPES,
} from '@techchange/components';

const axiosClient = axios.create({
	withCredentials: true,
});

const LIMIT = 15;

/**
 * React hook that keeps track of a paginated list of notifications.
 *
 * @param {Object} params                    The base query params.
 * @param {String} params.user_id            The user id.
 * @param {String} params.organization_id    The organization id.
 * @param {Array|String} params.include      The model(s) to include in the results (if any).
 * @param {String} nodeID                    The unique ID of the DOM element to listen for
 *                                           scroll events.
 *
 * @returns {Array} The data, error state, lazy load state, and a callback function to fetch
 *                  the data.
 */
const usePaginatedNotifications = (params = {}, nodeID) => {

	const [notifications, setNotifications] = useState(null);
	const [showError, setShowError] = useState(false);

	const [offset, setOffset] = useState(0);
	const [loadState, setLoadState] = useLazyLoad(nodeID);

	const { socket } = useSocketContext();

	const queryParams = Object.assign({}, params, {
		object_type: [
			LRS_OBJECT_TYPES.DISCUSSION_COMMENT,
			LRS_OBJECT_TYPES.DISCUSSION_REPLY,
			LRS_OBJECT_TYPES.PERMISSION,
			LRS_OBJECT_TYPES.USER,
			LRS_OBJECT_TYPES.EVENT
		],
		sort_by: 'created_at',
		sort_order: 'DESC',
		limit: LIMIT,
		include: ['actors', 'sessions'],
	});

	const fetchData = () => {

		axiosClient.get('/api/v2/notifications', { params: queryParams }).then((response) => {

			setLoadState((prevLoadState) => Object.assign({}, prevLoadState, {
				ready: true,
				loading: false,
				loadedAll: response.data.length < LIMIT,
			}));

			setOffset(offset + LIMIT);

			setNotifications(response.data);

		}).catch((error) => {

			console.error(error);
			setShowError(true);

		});

	};

	// Handle lazy loads triggered by scrolling down the list of notifications
	useEffect(() => {

		if (!loadState.loading) {
			return;
		}

		// Update the query params to include the pagination offset
		const finalQueryParams = Object.assign({}, queryParams, {
			offset,
		});

		axiosClient.get('/api/v2/notifications', { params: finalQueryParams }).then((response) => {

			setLoadState((prevLoadState) => Object.assign({}, prevLoadState, {
				ready: true,
				loading: false,
				loadedAll: response.data.length < LIMIT,
			}));

			setOffset(offset + LIMIT);

			setNotifications((prev) => prev.concat(response.data));

		}).catch((error) => {

			console.error(error);
			setShowError(true);

		});

	}, [loadState.loading, offset, queryParams, setLoadState]);

	// Listen for real-time changes to notifications
	useEffect(() => {

		// Don't add real-time listener until initial load of notifications
		// has been performed
		if (!notifications) {
			return () => {};
		}

		const handleRealTimeUpdate = (notification) => {

			const newNotification = { ...notification };

			// Check that the new notification is for the current user
			if (newNotification.user_id !== params.user_id ||
				newNotification.organization_id !== params.organization_id) {
				return;
			}

			const dataPromises = [];

			// Fetch user data for notification actor if needed
			if (newNotification.actor_id) {

				dataPromises.push(axiosClient.get(`/api/v2/users/${newNotification.actor_id}`));

			} else {

				dataPromises.push(new Promise((resolve) => resolve({ data: undefined })));

			}

			// Fetch session data for notification scope if needed
			if (newNotification.scope === SCOPES.SESSION) {

				dataPromises.push(axiosClient.get(`/api/v2/sessions/${newNotification.scope_id}`));

			} else {

				dataPromises.push(new Promise((resolve) => resolve({ data: undefined })));

			}

			Promise.all(dataPromises).then(([{ data: actor }, { data: session }]) => {

				newNotification.actor = actor;
				newNotification.session = session;

				setNotifications((prev) => {
					return [newNotification, ...prev];
				});

				setOffset((prev) => prev + 1);

			});

		};

		// Use a socket to start listening for real-time changes
		socket?.on('create:notification', handleRealTimeUpdate);

		// Clean up the socket to stop listening for real-time changes
		return () => socket?.removeListener('create:notification', handleRealTimeUpdate);

	}, [notifications, params.user_id, params.organization_id, socket]);

	return [notifications, setNotifications, showError, loadState, fetchData];

};

export default usePaginatedNotifications;
